/**
 * Mock of the K8 custom-role routes for `VITE_MOCK=1` (real routes:
 * `apps/app/src/routes/customRoles.ts` and `PUT /api/users/:id/custom-role`
 * in `users.ts`). In memory only; same rules and error codes as the server.
 */
import type { Capability, CustomRole, Role, TenantUser } from '../../types/api';

const CAPABILITIES: Capability[] = [
  'campaigns.edit',
  'campaigns.test_send',
  'campaigns.send',
  'templates.edit',
  'contacts.edit',
  'forms.edit',
  'ai.use',
  'settings.edit',
];
const MAX_ROLES = 20;

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function seedRoles(): Map<string, Omit<CustomRole, 'memberCount'>> {
  const now = Date.now();
  return new Map([
    [
      'cr-1',
      {
        id: 'cr-1',
        name: 'Copywriter',
        baseRole: 'editor',
        denied: ['campaigns.send', 'campaigns.test_send', 'settings.edit'],
        createdAt: now - 7 * 86_400_000,
        updatedAt: now - 7 * 86_400_000,
      },
    ],
  ]);
}

let roles = seedRoles();
let assignments = new Map<string, string>([['u-anna', 'cr-1']]);
let ownDenied: Capability[] = [];
let nextId = 2;

export function resetMockCustomRoles(): void {
  roles = seedRoles();
  assignments = new Map([['u-anna', 'cr-1']]);
  ownDenied = [];
  nextId = 2;
}

/** What `/auth/me` reports for the signed-in mock user (tests can set it). */
export function mockOwnDeniedCapabilities(): Capability[] {
  return ownDenied;
}
export function setMockOwnDeniedCapabilities(next: Capability[]): void {
  ownDenied = next;
}

/** The member list with each member's custom role, for `GET /users`. */
export function withCustomRoles(users: TenantUser[]): TenantUser[] {
  return users.map((u) => ({ ...u, customRoleId: assignments.get(u.id) ?? null }));
}

function withCount(r: Omit<CustomRole, 'memberCount'>): CustomRole {
  let n = 0;
  for (const id of assignments.values()) if (id === r.id) n++;
  return { ...r, memberCount: n };
}

function parseInput(body: unknown) {
  const b = (body ?? {}) as { name?: unknown; baseRole?: unknown; denied?: unknown };
  const name = typeof b.name === 'string' ? b.name.trim() : '';
  if (name.length < 1 || name.length > 40) return null;
  if (b.baseRole !== 'editor' && b.baseRole !== 'viewer') return null;
  if (!Array.isArray(b.denied)) return null;
  if (!b.denied.every((c) => CAPABILITIES.includes(c as Capability))) return null;
  const denied = [...new Set(b.denied as Capability[])].sort();
  return { name, baseRole: b.baseRole, denied } as const;
}

function nameTaken(name: string, exceptId?: string): boolean {
  for (const r of roles.values()) {
    if (r.id !== exceptId && r.name.toLowerCase() === name.toLowerCase()) return true;
  }
  return false;
}

export function handleMockCustomRoles(
  method: string,
  pathname: string,
  body: unknown,
  mockRole: Role,
  users: Map<string, TenantUser>,
): Response | null {
  const assignMatch = /^\/users\/([^/]+)\/custom-role$/.exec(pathname);
  if (assignMatch && method === 'PUT') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const userId = assignMatch[1] as string;
    const member = users.get(userId);
    if (!member) return json({ error: 'not_found' }, 404);
    const roleId = ((body ?? {}) as { customRoleId?: string | null }).customRoleId ?? null;
    if (roleId === null) {
      const had = assignments.delete(userId);
      return json({
        status: had ? 'cleared' : 'unchanged',
        id: userId,
        role: member.role,
        customRoleId: null,
      });
    }
    const role = roles.get(roleId);
    if (!role) return json({ error: 'role_not_found' }, 404);
    if (member.role === 'admin') return json({ error: 'target_is_admin' }, 409);
    assignments.set(userId, roleId);
    users.set(userId, { ...member, role: role.baseRole });
    return json({ status: 'assigned', id: userId, role: role.baseRole, customRoleId: roleId });
  }

  if (pathname !== '/custom-roles' && !pathname.startsWith('/custom-roles/')) return null;
  if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);

  if (pathname === '/custom-roles' && method === 'GET') {
    const list = Array.from(roles.values())
      .map(withCount)
      .sort((a, b) => a.name.localeCompare(b.name));
    return json({
      roles: list,
      capabilities: CAPABILITIES,
      bases: ['editor', 'viewer'],
      maxRoles: MAX_ROLES,
    });
  }
  if (pathname === '/custom-roles' && method === 'POST') {
    const input = parseInput(body);
    if (!input) return json({ error: 'invalid_request' }, 400);
    if (roles.size >= MAX_ROLES) return json({ error: 'limit', max: MAX_ROLES }, 409);
    if (nameTaken(input.name)) return json({ error: 'duplicate_name' }, 409);
    const now = Date.now();
    const role = { id: `cr-${nextId++}`, ...input, createdAt: now, updatedAt: now };
    roles.set(role.id, role);
    return json({ role: withCount(role) }, 201);
  }
  const idMatch = /^\/custom-roles\/([^/]+)$/.exec(pathname);
  if (!idMatch) return json({ error: 'not_found' }, 404);
  const id = idMatch[1] as string;
  const existing = roles.get(id);
  if (!existing) return json({ error: 'not_found' }, 404);

  if (method === 'PUT') {
    const input = parseInput(body);
    if (!input) return json({ error: 'invalid_request' }, 400);
    if (nameTaken(input.name, id)) return json({ error: 'duplicate_name' }, 409);
    const updated = { ...existing, ...input, updatedAt: Date.now() };
    roles.set(id, updated);
    if (existing.baseRole !== input.baseRole) {
      for (const [userId, roleId] of assignments) {
        const u = users.get(userId);
        if (roleId === id && u && u.role !== 'admin')
          users.set(userId, { ...u, role: input.baseRole });
      }
    }
    return json({ role: withCount(updated) });
  }
  if (method === 'DELETE') {
    const count = withCount(existing).memberCount;
    if (count > 0) return json({ error: 'in_use', memberCount: count }, 409);
    roles.delete(id);
    return json({ status: 'deleted' });
  }
  return json({ error: 'not_found' }, 404);
}
